import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";

const posts = [
  {
    id: "gst-simplified",
    category: "Tax & Legal",
    title: "GST for small businesses: what actually matters in 2026",
    excerpt:
      "A practical walkthrough of what's changed, what's still the same, and the three filings most founders get wrong.",
    date: "Mar 12, 2026",
    readTime: "6 min read",
    image:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=900&q=70",
  },
  {
    id: "real-estate-dd",
    category: "Real Estate",
    title: "Buying your first commercial property? Read this first.",
    excerpt:
      "The due-diligence checklist we run on every deal — from title verification to hidden encumbrances.",
    date: "Feb 28, 2026",
    readTime: "8 min read",
    image:
      "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=900&q=70",
  },
  {
    id: "startup-finance",
    category: "Finance",
    title: "Monthly closings: the quiet habit that separates real companies",
    excerpt:
      "Why closing the books on the same day every month changes how you run the rest of your business.",
    date: "Feb 10, 2026",
    readTime: "5 min read",
    image:
      "https://images.unsplash.com/photo-1553729459-efe14ef6055d?auto=format&fit=crop&w=900&q=70",
  },
  {
    id: "local-marketing",
    category: "Marketing",
    title: "Local ads that convert: what we've learned spending our clients' money",
    excerpt:
      "Seven patterns we see in campaigns that actually bring in walk-ins — and three myths we've put to rest.",
    date: "Jan 22, 2026",
    readTime: "7 min read",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=900&q=70",
  },
  {
    id: "automation-small-teams",
    category: "IT Solutions",
    title: "The automation stack a 10-person team actually needs",
    excerpt:
      "Forget the 'AI-first' hype. Here's what to automate first, what to leave alone, and how to keep your data yours.",
    date: "Jan 08, 2026",
    readTime: "6 min read",
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=900&q=70",
  },
  {
    id: "education-institute-setup",
    category: "Education",
    title: "Opening a coaching institute in a tier-2 city: a 90-day playbook",
    excerpt:
      "From licensing and lease negotiation to the first cohort — the operating checklist we've shipped a dozen times.",
    date: "Dec 18, 2025",
    readTime: "9 min read",
    image:
      "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=900&q=70",
  },
];

const BlogCard = ({ p, index }) => {
  const onClick = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`blog-card-${p.id}`}
      className="group relative rounded-3xl overflow-hidden glass-strong flex flex-col cursor-pointer hover:-translate-y-1.5 transition-transform duration-500"
      onClick={onClick}
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={p.image}
          alt={p.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,130,216,0.18) 0%, rgba(0,0,0,0) 45%, rgba(255,255,255,0) 65%, rgba(255,255,255,0.85) 100%)",
          }}
        />
        <div className="dark:hidden absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-background to-transparent" />
        <div className="hidden dark:block absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[hsl(var(--card))] to-transparent" />
        <span
          className="absolute top-4 left-4 inline-flex items-center text-[10px] font-semibold px-2.5 py-1 rounded-full text-white tracking-[0.1em] uppercase"
          style={{ background: "linear-gradient(135deg,#00B4F0,#0A82D8)" }}
        >
          {p.category}
        </span>
      </div>
      <div className="flex-1 flex flex-col px-6 pb-6 -mt-1">
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span>{p.date}</span>
          <span className="h-1 w-1 rounded-full bg-muted-foreground/60" />
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {p.readTime}
          </span>
        </div>
        <h3 className="mt-3 font-display font-semibold text-[1.2rem] md:text-[1.3rem] leading-snug tracking-tight">
          {p.title}
        </h3>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">
          {p.excerpt}
        </p>
        <div className="mt-auto pt-5 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A82D8]">
            Read more
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
          <div className="h-px w-10 bg-gradient-to-r from-[#00B4F0] to-[#0A82D8] group-hover:w-20 transition-all duration-500" />
        </div>
      </div>
    </motion.article>
  );
};

const Blog = () => {
  return (
    <section
      id="blog"
      data-testid="blog-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="flex items-end justify-between gap-6 mb-12"
        >
          <div className="max-w-3xl">
            <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#0A82D8]">
              From the team
            </span>
            <h2 className="mt-3 font-display font-semibold text-4xl md:text-5xl lg:text-[3.25rem] tracking-tighter leading-[1.05]">
              Notes, playbooks and <br className="hidden sm:block" />
              <span className="text-gradient-cyan">things we've learned.</span>
            </h2>
            <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-xl">
              Short reads from our day-to-day work across tax, finance, real estate, IT and more. Written by the people doing the work.
            </p>
          </div>
          <button
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-[#0A82D8] hover:gap-3 transition-all"
            data-testid="blog-all-cta"
          >
            Subscribe for more
            <ArrowUpRight className="h-4 w-4" />
          </button>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {posts.map((p, i) => (
            <BlogCard key={p.id} p={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Blog;
